import { useContext, useMemo } from "react";

import { AnyPartnerConfiguration } from "@gamedev-web/gamedev-sdk-core";
import { lceFetching, LCEType } from "@gaming/lce";

import { PartnerContext, PartnerContextType } from "./PartnerContext";
import { PartnerConfigLce } from "./types";

/**
 * A hook to return the Partner Context value.
 *
 * ONLY FOR PLATFORM USE.
 *
 * For any partner needs use usePartnerConfig.
 */
export function usePartnerContext(): PartnerContextType {
  return useContext(PartnerContext);
}

type LoadingState = {
  lce: PartnerConfigLce;
  isLoading: true;
  isFetching: true;
  isError: false;
  error?: undefined;
};

type SuccessState = {
  lce: PartnerConfigLce;
  isLoading: false;
  isFetching: false;
  isError: false;
  error?: undefined;
};

type SuccessStateRefetching = {
  lce: PartnerConfigLce;
  isLoading: false;
  isFetching: true;
  isError: false;
  error?: undefined;
};

type ErrorState = {
  lce: PartnerConfigLce;
  isLoading: false;
  isFetching: false;
  isError: true;
  error: Error;
};

type PartnerConfigState = LoadingState | ErrorState | SuccessState | SuccessStateRefetching;

/**
 * A hook to select the partner config for the partner id and initiate loading it.
 * Loading the partner includes but is not limited to loading the partner
 * redux and strings.
 *
 * For typescript to a properly resolve `error` as non-null when `isError` is true,
 * first pull out the state like `[, state] = usePartnerConfig("core")`, then use
 * `isError` and `error`: `const { isError, error } = state;` `error` will resolve
 * as non-null when `isError` is true. That, or we can do
 * `const { isError, error } } = usePartnerConfig("core")[1];`
 *
 * @param partnerId The id of the Partner Configuration to load.
 * @returns A tuple of the partner configuration and its loading state.
 */
export function usePartnerConfig(partnerId: string): [AnyPartnerConfiguration, PartnerConfigState] {
  const context = usePartnerContext();
  const loadPartner = context.loadPartner;
  const partnerLce = context.partnerLoadingState[partnerId];
  const partnerConfig = context.partnerConfigs[partnerId];

  // We can call this here because it is an async function and
  // won't affect any subscribers to changes on this render.
  if (partnerLce.type === LCEType.NotRequested) {
    loadPartner(partnerId);
  }

  return useMemo(() => {
    const isLoading =
      // Initial state. We will begin fetching.
      partnerLce.type === LCEType.NotRequested ||
      // Initial fetching state or fetching after error.
      // We are not refetching after previous success.
      (partnerLce.type === LCEType.Fetching && !partnerLce.previousContent);

    const result = {
      // Coerce lce to Fetching so as to reduce possible states
      lce: partnerLce.type === LCEType.NotRequested ? lceFetching() : partnerLce,
      isLoading,
      // Merge NotRequested with Fetching to prevent needless downstream updates.
      isFetching: partnerLce.type === LCEType.Fetching || partnerLce.type === LCEType.NotRequested,
      isError: partnerLce.type === LCEType.Error,
      error: partnerLce.type === LCEType.Error ? partnerLce.error : undefined,
    } as PartnerConfigState;

    return [partnerConfig, result];
  }, [partnerConfig, partnerLce]);
}
